import { useMemo, useState } from 'react';
import { Check, Database, Layers, Pencil, ShieldCheck } from 'lucide-react';
import type { Connection } from '../api/client';

/** A short reassurance about a security property. */
export function SafetyNote({ children }: { children: React.ReactNode }) {
  return (
    <p className="safety-note">
      <ShieldCheck size={15} />
      <span>{children}</span>
    </p>
  );
}

/**
 * Chooses which databases a token may reach.
 *
 * The important thing this communicates is that reach is chosen *per token*, so
 * a token handed to a chat client is not automatically a key to every database
 * on the account. "All databases" is offered because it is a legitimate choice,
 * but it is not the default: a token that quietly covers everything is the
 * behaviour this picker exists to stop.
 */
export function ConnectionScopePicker({
  connections,
  value,
  onChange,
  disabled = false,
  legend = 'Databases this token can reach',
}: {
  connections: Connection[];
  /** null = every database. An array = exactly those. */
  value: string[] | null;
  onChange: (next: string[] | null) => void;
  disabled?: boolean;
  legend?: string;
}) {
  // Explicit 'all' / 'some' rather than deriving it from the array, because
  // "every box ticked" and "all databases" mean the same thing to the backend
  // but the user made different choices to get there, and an empty account must
  // not render as "all".
  const [mode, setMode] = useState<'all' | 'some'>(value === null ? 'all' : 'some');

  const ids = useMemo(() => connections.map((c) => c.id), [connections]);

  const toggle = (id: string) => {
    const current = value ?? [];
    onChange(current.includes(id) ? current.filter((v) => v !== id) : [...current, id]);
  };

  const selectAll = () => onChange([...ids]);
  const selectNone = () => onChange([]);

  if (connections.length === 0) {
    return (
      <div className="scope-picker">
        <p className="scope-empty">
          <Database size={15} />
          <span>
            You have no databases yet. Select a document generator above to create a
            generator-only token, or add a database to enable database tools.
          </span>
        </p>
      </div>
    );
  }

  return (
    <fieldset className="scope-picker" disabled={disabled}>
      <legend>{legend}</legend>

      <label className="scope-option">
        <input
          type="radio"
          name="scope-mode"
          checked={mode === 'all'}
          onChange={() => {
            setMode('all');
            onChange(null);
          }}
        />
        <Layers size={16} />
        <span>
          <strong>All my databases ({connections.length})</strong>
          <small>
            This token reaches everything on the account, including databases you
            add later.
          </small>
        </span>
      </label>

      <label className="scope-option">
        <input
          type="radio"
          name="scope-mode"
          checked={mode === 'some'}
          onChange={() => {
            setMode('some');
            // Entering "some" starts from everything ticked, which is the
            // least surprising reading of "let me choose": narrow from there
            // rather than starting from nothing.
            onChange(value ?? [...ids]);
          }}
        />
        <Check size={16} />
        <span>
          <strong>Only selected databases</strong>
          <small>
            The agent sees just these. Databases added later stay out until you
            edit this token.
          </small>
        </span>
      </label>

      {mode === 'some' && (
        <div className="scope-list">
          <div className="scope-list-actions">
            <button type="button" className="link" onClick={selectAll}>
              Select all
            </button>
            <button type="button" className="link" onClick={selectNone}>
              Clear
            </button>
            <span className="muted small">
              {(value ?? []).length} of {connections.length} selected
            </span>
          </div>

          {connections.map((connection) => {
            const checked = (value ?? []).includes(connection.id);
            return (
              <label key={connection.id} className="scope-item">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(connection.id)}
                />
                <span className="scope-item-body">
                  <strong>{connection.name}</strong>
                  <small>
                    {connection.database_type} · {connection.database_name}
                  </small>
                </span>
                {connection.status === 'error' && (
                  <span className="badge badge-warn">Unreachable</span>
                )}
              </label>
            );
          })}
        </div>
      )}
    </fieldset>
  );
}

/**
 * Read-only summary of a token's reach, with an inline edit affordance.
 *
 * Shown in the token table so the scope is visible at a glance: a list of
 * "Active" tokens tells a user nothing about what each one can actually reach.
 */
export function ScopeSummary({
  connections,
  connectionIds,
  allConnections,
  onEdit,
}: {
  connections: Connection[];
  connectionIds: string[] | null;
  allConnections: boolean;
  onEdit?: () => void;
}) {
  if (allConnections) {
    return (
      <span className="scope-summary">
        <Layers size={13} /> All databases
        {onEdit && (
          <button type="button" className="link" onClick={onEdit}>
            <Pencil size={12} /> Change
          </button>
        )}
      </span>
    );
  }

  const selected = connections.filter((c) => connectionIds?.includes(c.id));
  const missing = (connectionIds?.length ?? 0) - selected.length;

  if (selected.length === 0) {
    return (
      <span className="scope-summary scope-none">
        Document generators only — no database access
        {onEdit && (
          <button type="button" className="link" onClick={onEdit}>
            <Pencil size={12} /> Change
          </button>
        )}
      </span>
    );
  }

  return (
    <span className="scope-summary">
      <Database size={13} />
      {selected.map((c) => c.name).join(', ')}
      {/* A deleted database leaves an id with no name; say so rather than
          silently under-reporting what the token still points at. */}
      {missing > 0 && (
        <small className="muted">+{missing} deleted</small>
      )}
      {onEdit && (
        <button type="button" className="link" onClick={onEdit}>
          <Pencil size={12} /> Change
        </button>
      )}
    </span>
  );
}
